/**
 * Condition Evaluator
 *
 * Safely evaluates condition expressions for conditional workflow step execution.
 *
 * @packageDocumentation
 */

import type { ExecutionContext } from '../types/execution';

/**
 * Context object available to condition expressions.
 */
export interface ConditionContext {
  /** The workflow's initial input */
  input: any;
  /** Outputs from completed steps, keyed by step name */
  steps: Record<string, { output: any }>;
}

/**
 * Result of condition evaluation.
 */
export interface ConditionResult {
  /** Whether the condition passed (step should execute) */
  shouldExecute: boolean;
  /** Error if evaluation failed */
  error?: string;
}

/**
 * Evaluates a condition expression to determine if a step should execute.
 *
 * The condition is a JavaScript expression that can reference:
 * - `input`: The workflow's initial input
 * - `steps`: Object containing outputs from completed steps
 *
 * @param condition - JavaScript expression string
 * @param context - Execution context with input and step outputs
 * @returns Result indicating whether step should execute
 *
 * @example
 * ```typescript
 * // Simple comparison
 * evaluateCondition("steps.classify.output.confidence > 0.8", context);
 *
 * // String matching
 * evaluateCondition("steps.classify.output.category === 'urgent'", context);
 *
 * // Input-based condition
 * evaluateCondition("input.priority === 'high'", context);
 *
 * // Complex condition
 * evaluateCondition("steps.extract.output && steps.extract.output.total > 1000", context);
 * ```
 */
export function evaluateCondition(
  condition: string,
  context: ExecutionContext
): ConditionResult {
  // Build the condition context
  const conditionContext = buildConditionContext(context);

  try {
    // Create a safe evaluation function
    // Note: This uses Function constructor which is safer than eval
    // but still allows arbitrary code execution. In production,
    // consider using a sandboxed evaluator like vm2 or safe-eval.
    const evaluator = new Function(
      'input',
      'steps',
      `"use strict"; return (${condition});`
    );

    const result = evaluator(conditionContext.input, conditionContext.steps);

    // Coerce result to boolean
    return {
      shouldExecute: Boolean(result),
    };
  } catch (error) {
    return {
      shouldExecute: false,
      error: `Condition evaluation failed: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}

/**
 * Builds a condition context from the execution context.
 *
 * @param context - Execution context
 * @returns Condition context with input and steps
 */
function buildConditionContext(context: ExecutionContext): ConditionContext {
  // Build steps object from step outputs
  const steps: Record<string, { output: any }> = {};

  // Get all step outputs from the context
  for (const [stepName, output] of context.stepOutputs.entries()) {
    steps[stepName] = { output };
  }

  return {
    input: context.initialInput,
    steps,
  };
}

/**
 * Validates that a condition expression is syntactically valid.
 *
 * @param condition - JavaScript expression string
 * @returns True if valid, throws Error if invalid
 */
export function validateCondition(condition: string): boolean {
  try {
    // Try to create the function to check syntax
    new Function('input', 'steps', `"use strict"; return (${condition});`);
    return true;
  } catch (error) {
    throw new Error(
      `Invalid condition expression: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}
